<template>
  <div ref="container" class="resource-taggings" data-loaded="false" @click.stop>
    <AccordionWrapper v-if="displayInAccordion">
      <cv-accordion-item :open="expandAccordion">
        <template #title>
          <span class="title">{{ accordionTitle }}</span>
        </template>
        <template #content>
          <div ref="container" class="resource-taggings" data-loaded="false" @click.stop>
            <resource-taggings-view
              v-if="!isEditMode"
              :resource-type="resourceType"
              :resource-uuid="resourceUuid"
              :resource-status="lessonResourceStatus"
              :hide-empty-indicator="hideEmptyIndicator"
              :is-adaptive-section="isAdaptiveSection"
              :single-empty-indicator="singleEmptyIndicator"
              @loaded="handleLoaded"
            />
            <resource-taggings-edit
              v-else
              :resource-type="resourceType"
              :resource-uuid="resourceUuid"
              :resource-status="lessonResourceStatus"
              :allow-multiple-select-topic="allowMultipleSelectTopic"
              :is-adaptive-section="isAdaptiveSection"
              :show-section-tags-message="showSectionTagsMessage"
              :skip-fetch="skipFetch"
              :content-map-warning-context="contentMapWarningContext"
              selection-kind="white"
              accordion-kind="gray"
              @loaded="handleLoaded"
            />
          </div>
        </template>
      </cv-accordion-item>
    </AccordionWrapper>
    <template v-else>
      <div ref="container" class="resource-taggings" data-loaded="false" @click.stop>
        <resource-taggings-view
          v-if="!isEditMode"
          :resource-type="resourceType"
          :resource-uuid="resourceUuid"
          :resource-status="lessonResourceStatus"
          :hide-empty-indicator="hideEmptyIndicator"
          :is-adaptive-section="isAdaptiveSection"
          :single-empty-indicator="singleEmptyIndicator"
          @loaded="handleLoaded"
        />
        <resource-taggings-edit
          v-else
          :resource-type="resourceType"
          :resource-uuid="resourceUuid"
          :resource-status="lessonResourceStatus"
          :allow-multiple-select-topic="allowMultipleSelectTopic"
          :is-adaptive-section="isAdaptiveSection"
          :skip-fetch="skipFetch"
          :content-map-warning-context="contentMapWarningContext"
          @loaded="handleLoaded"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import ResourceTaggingsView from "@modules/editor/components/resource-taggings/ResourceTaggingsView.vue";
import ResourceTaggingsEdit from "@modules/editor/components/resource-taggings/ResourceTaggingsEdit.vue";
import DOMUtils from "@utils/dom.utils";
import { computed, useTemplateRef } from "vue";
import { useStore } from "vuex";
import { NAMESPACE } from "@modules/attempt/service/attempt.constant.ts";
import type { ContentMapWarningContext } from "@modules/resource-tagging-selection/service/resource.tagging.selection.constant.ts";

interface Props {
  resourceUuid: string;
  resourceType: string;
  isEditMode: boolean;
  resourceStatus?: string;
  hideEmptyIndicator?: boolean;
  allowMultipleSelectTopic?: boolean;
  isAdaptiveSection?: boolean;
  singleEmptyIndicator?: boolean;
  displayInAccordion?: boolean;
  showSectionTagsMessage?: boolean;
  accordionTitle?: string;
  skipFetch?: boolean;
  contentMapWarningContext?: ContentMapWarningContext;
}

const props = withDefaults(defineProps<Props>(), {
  resourceStatus: undefined,
  hideEmptyIndicator: false,
  allowMultipleSelectTopic: true,
  isAdaptiveSection: false,
  singleEmptyIndicator: false,
  displayInAccordion: false,
  showSectionTagsMessage: false,
  accordionTitle: "",
  skipFetch: false,
  contentMapWarningContext: undefined,
});

const emit = defineEmits<{
  loaded: [];
}>();

const container = useTemplateRef("container");

const store = useStore();
const lesson = computed(() => store.getters[`${NAMESPACE.RESOURCE}/getLesson`]);

// Computed
const expandAccordion = computed(() => {
  return props.isAdaptiveSection && props.isEditMode;
});

const lessonResourceStatus = computed(() => {
  // If resourceStatus prop is defined by parent component, use it.
  if (props.resourceStatus) {
    return props.resourceStatus;
  }
  // Otherwise, prop will be undefined, hence fetch from resource store.
  return lesson.value?.status;
});

// Methods
const handleLoaded = function () {
  emit("loaded");
  DOMUtils.setAttr(container.value, "data-loaded", true);
};
</script>

<style lang="scss">
@import "@styles/layout/breakpoint";
@import "@styles/abstracts/variables";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.resource-taggings {
  width: 100%;

  .resource-tagging {
    > .field-label {
      ~ .subject-level-and-stream-selection {
        margin-top: $spacing-03;
      }
    }
  }

  .actions {
    flex-flow: row wrap;
    justify-content: center;
    margin: $spacing-05 0;

    @include media("mobile") {
      flex-flow: column;
      align-items: center;
    }

    .bx--btn {
      max-width: 18rem;
      margin: $spacing-03 0;

      &.add-lesson-tag {
        width: 13rem;
        line-height: 1rem;

        &:only-child {
          width: auto;
        }

        .icon {
          margin-right: $spacing-03;
        }
      }

      &:not(:first-child) {
        @include media("tablet-min") {
          margin-left: $spacing-05;
        }
      }
    }
  }

  .tagging {
    .icon {
      position: absolute;
      right: 2.5rem;
      top: 0;
    }

    .bx--accordion__content {
      padding: 0;
    }
  }

  .lo-wrapper {
    display: flex;
    align-items: center;
    padding: $spacing-03 0;
    gap: $spacing-02;

    + .field-value {
      margin-top: 0;
    }

    .sls-icon {
      min-width: 24px;
    }

    .info-tooltip {
      color: $icon-01;

      .sls-icon {
        [stroke] {
          stroke: currentColor;
        }

        [fill] {
          fill: currentColor;
        }
      }
    }

    .bx--toggle-input__label {
      @include carbon--type-style("body-short-01");
      display: flex;
      flex-flow: row-reverse;
      align-items: center;
      gap: $spacing-03;
      color: $text-02;
    }

    .toggle {
      max-width: fit-content;

      &.bx--toggle-input--small {
        & + .bx--toggle-input__label {
          .bx--toggle__switch {
            margin-top: 0;
            flex: none;
          }
        }
      }

      .bx--toggle__text--off,
      .bx--toggle__text--on {
        display: none;
      }
    }
  }

  .lo-display-name {
    @include carbon--type-style("label-01", true);
    display: block;
    color: $text-02;
    flex: 1 1 100%;
    margin-top: $spacing-02;
  }

  .field-group {
    display: flex;
    align-items: center;
    gap: 0 $spacing-05;
    margin-bottom: $spacing-05;

    .selection-wrapper {
      flex: 1;
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      grid-gap: $spacing-05;

      @include media("tablet-max") {
        display: unset;
      }

      .field-set {
        &:last-child {
          margin-bottom: 0;
        }

        @include media("desktop") {
          margin-bottom: 0;
        }
      }

      .field-input {
        margin-top: 0;
      }
    }

    .icon-button {
      grid-row: 2;
      padding: $spacing-03;
    }
  }

  .tag-content {
    padding: $spacing-05;

    > .field-group {
      align-items: flex-start;

      @include media("tablet-max") {
        flex-flow: column;
      }

      .field-set {
        flex: 1;
      }
    }
  }

  .topic.tree {
    @include media("desktop") {
      border-bottom-right-radius: 10px;
    }

    .field-set {
      display: flex;
      flex-flow: column nowrap;
      height: 100%;
    }

    .field-input {
      flex: 1 1 0%;
      display: flex;
      flex-flow: column nowrap;
      margin-top: 0;
    }

    .info {
      margin-bottom: $spacing-05;
      @include carbon--type-style("helper-text-01", true);
    }

    .message {
      @include carbon--type-style("body-long-01", true);
    }

    .tree-wrapper.empty {
      flex: 1 1 0%;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: $spacing-06;
      background-color: $ui-background;
      border-radius: 10px;
      color: $disabled-02;
      text-align: center;
    }
  }

  > .accordion-wrapper {
    .resource-tagging-view-item {
      background-color: $ui-background;
    }
  }
}
</style>
